/**
 * TipTap extension that inserts visual page-break spacers between
 * top-level document nodes.
 *
 * All layout math lives in {@link ./page-layout.ts}; all dimension
 * constants come from {@link ./page-config.ts}.  This file only
 * handles TipTap/ProseMirror integration: scheduling measurements,
 * creating widget decorations, and reporting page count.
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import {
  type PageConfig,
  US_LETTER,
  spacerHeight,
} from "./page-config";
import { measureBlocks, computeBreaks } from "./page-layout";

// Re-export config helpers so existing consumers keep working.
export {
  type PageConfig,
  US_LETTER,
  A4,
  contentHeight,
  contentWidth,
  spacerHeight,
  totalPagesHeight,
  contentAreaInnerMinHeight,
  applyPageCSSVars,
} from "./page-config";

// ── Plugin internals ───────────────────────────────────────────────

const pageBreaksKey = new PluginKey("pageBreaks");

function createSpacerElement(_pageNumber: number, height: number): HTMLElement {
  const el = document.createElement("div");
  el.className = "docx-page-spacer";
  el.setAttribute("data-page-break", "true");
  el.style.height = `${height}px`;
  el.contentEditable = "false";
  return el;
}

function arrEq(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// ── Extension ──────────────────────────────────────────────────────

export interface PageBreaksOptions {
  pageConfig: PageConfig;
}

export interface PageBreaksStorage {
  pageCount: number;
  onPageCountChange: ((n: number) => void) | null;
}

export const PageBreaks = Extension.create<PageBreaksOptions, PageBreaksStorage>({
  name: "pageBreaks",

  addOptions() {
    return { pageConfig: US_LETTER };
  },

  addStorage() {
    return {
      pageCount: 1,
      onPageCountChange: null,
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    const config = this.options.pageConfig;
    const spacerH = spacerHeight(config);

    let decos = DecorationSet.empty;
    let lastPositions: number[] = [];
    let raf: number | null = null;

    return [
      new Plugin({
        key: pageBreaksKey,

        props: {
          decorations() {
            return decos;
          },
        },

        view(view) {
          function recalc() {
            const blocks = measureBlocks(view.dom);
            const breakPositions = computeBreaks(blocks, config);

            const docPositions: number[] = breakPositions.map((bp) => {
              try {
                const inside = view.posAtDOM(blocks[bp.blockIndex].element, 0);
                return view.state.doc.resolve(inside).before(1);
              } catch {
                return -1;
              }
            }).filter((p) => p >= 0);

            if (arrEq(docPositions, lastPositions)) return;
            lastPositions = docPositions;

            const pageCount = docPositions.length + 1;

            const widgets = docPositions.map((pos, i) =>
              Decoration.widget(
                pos,
                () => createSpacerElement(i + 2, spacerH),
                { side: -1, key: `pb-${i}` },
              ),
            );

            decos = DecorationSet.create(view.state.doc, widgets);

            if (pageCount !== storage.pageCount) {
              storage.pageCount = pageCount;
              storage.onPageCountChange?.(pageCount);
            }

            view.dispatch(view.state.tr.setMeta(pageBreaksKey, true));
          }

          function schedule() {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
              raf = null;
              recalc();
            });
          }

          schedule();

          const obs = new ResizeObserver(() => schedule());
          obs.observe(view.dom);

          return {
            update(v, prev) {
              if (!v.state.doc.eq(prev.doc)) {
                lastPositions = [];
                schedule();
              }
            },
            destroy() {
              if (raf) cancelAnimationFrame(raf);
              obs.disconnect();
            },
          };
        },
      }),
    ];
  },
});
